import { apiRequest } from './client.js';

type Tok = string | null | undefined;

const BASE = '/v1/m17/config';

/** t204_branding_type_master — ATL, BTL, POP/POSM, Others. */
export interface BrandingTypeRow {
  readonly brandingTypeId: string;
  readonly typeCode: string;
  readonly typeName: string;
  readonly description: string | null;
  readonly measurementUnit: string | null;
  readonly displayOrder: number | null;
  readonly recordStatusCode: string;
}

/** t603_branding_item_master — a subtype sitting under one type. */
export interface BrandingItemRow {
  readonly brandingItemId: string;
  readonly brandingTypeId: string;
  readonly itemCode: string;
  readonly itemName: string;
  readonly description: string | null;
  readonly uomId: string | null;
  readonly uomCode: string | null;
  readonly displayOrder: number | null;
  readonly isStandard: boolean;
  /** True where the work is done on the dealer's own property. */
  readonly dealerReferenceRequired: boolean;
  /** What each of a line's three dimensions measures for this sub-type. */
  readonly dim1Meaning: string;
  readonly dim2Meaning: string;
  readonly dim3Meaning: string;
  /** A completion of this sub-type cannot be submitted without a photograph. */
  readonly requiresPhoto: boolean;
  /** ...nor without the position it was done at. */
  readonly requiresGps: boolean;
  /** The shape this sub-type is drawn in on the activity map. */
  readonly mapShape: string;
  /** Held in the GST classification chain, joined on read. */
  readonly hsnSacCode: string | null;
  readonly hsnSacType: string | null;
  readonly recordStatusCode: string;
}

export interface CreateBrandingTypeInput {
  readonly typeCode: string;
  readonly typeName: string;
  readonly description?: string;
  readonly measurementUnit?: string;
}

export interface CreateBrandingItemInput {
  readonly brandingTypeId: string;
  readonly itemCode: string;
  readonly itemName: string;
  readonly description?: string;
  readonly uomId?: string;
  readonly displayOrder?: number;
  readonly dealerReferenceRequired?: boolean;
  readonly dim1Meaning?: string;
  readonly dim2Meaning?: string;
  readonly dim3Meaning?: string;
  readonly requiresPhoto?: boolean;
  readonly requiresGps?: boolean;
  readonly mapShape?: string;
  readonly hsnSacCode?: string;
}

type Page<T> = { data: readonly T[]; total: number };

// limit is deliberately high: this master is a few dozen rows and the screen
// groups subtypes under their type, so a second page would split a group.
export function listBrandingTypes(token: Tok, limit = 200): Promise<Page<BrandingTypeRow>> {
  return apiRequest(`${BASE}/branding-types?page=1&limit=${limit}`, { token });
}

export function listBrandingItems(
  token: Tok,
  limit = 500,
  /** Withdrawn subtypes too, so the screen can offer to reinstate one. */
  includeWithdrawn = false,
): Promise<Page<BrandingItemRow>> {
  const qs = new URLSearchParams({ page: '1', limit: String(limit) });
  if (includeWithdrawn) qs.set('includeWithdrawn', 'true');
  return apiRequest(`${BASE}/branding-items?${qs.toString()}`, { token });
}

export function createBrandingType(
  input: CreateBrandingTypeInput,
  token: Tok,
): Promise<BrandingTypeRow> {
  return apiRequest(`${BASE}/branding-types`, { method: 'POST', body: input, token });
}

export function updateBrandingType(
  id: string,
  input: Partial<CreateBrandingTypeInput>,
  token: Tok,
): Promise<BrandingTypeRow> {
  return apiRequest(`${BASE}/branding-types/${id}`, { method: 'PUT', body: input, token });
}

export function createBrandingItem(
  input: CreateBrandingItemInput,
  token: Tok,
): Promise<BrandingItemRow> {
  return apiRequest(`${BASE}/branding-items`, { method: 'POST', body: input, token });
}

export function updateBrandingItem(
  id: string,
  input: Partial<Omit<CreateBrandingItemInput, 'brandingTypeId'>>,
  token: Tok,
): Promise<BrandingItemRow> {
  return apiRequest(`${BASE}/branding-items/${id}`, { method: 'PUT', body: input, token });
}

/** Withdraws a subtype from the pickers without destroying what used it. */
export function deactivateBrandingItem(id: string, token: Tok): Promise<void> {
  return apiRequest(`${BASE}/branding-items/${id}`, { method: 'DELETE', token });
}

export function reactivateBrandingItem(id: string, token: Tok): Promise<BrandingItemRow> {
  return apiRequest(`${BASE}/branding-items/${id}/reactivate`, { method: 'POST', token });
}

/** t602_uom_master, needed because a subtype is measured in something. */
export interface UomRow {
  readonly uomId: string;
  readonly uomCode: string;
  readonly uomName: string;
  readonly recordStatusCode: string;
}

export function listUoms(token: Tok, limit = 200): Promise<Page<UomRow>> {
  return apiRequest(`${BASE}/uoms?page=1&limit=${limit}`, { token });
}
